import { CherryHubAdminApi } from "./adminApiModels";
import axios, { Axios } from "axios";
import { User } from "firebase/auth";
import { ApiClient, apiResult, ApiResult } from "./dataApiClient";
import { InvitationType } from "../enums/InvitationType";
import { ReportType } from "../enums/ReportType";
import { CherryHubDataApi } from "./dataApiModels";

declare const ADMIN_PREFIX: string;

export class AdminApiClient implements ApiClient {
  adminApiClient: Axios;

  constructor(user: User) {
    this.adminApiClient = axios.create({
      baseURL: `${API_BASE_URL}${this.getPrefixRoute()}`,
      // HTTP errors will not throw exceptions
      validateStatus: () => true,
    });

    this.adminApiClient.interceptors.request.use((config) => {
      // Fetches the current access token. If it has expired or is close to exiring a new token will be fetched.
      return user.getIdTokenResult().then((idToken) => {
        if (!config.headers) {
          config.headers = {};
        }
        config.headers!.Authorization = `Bearer ${idToken.token}`;
        return config;
      });
    });
  }

  getPrefixRoute(): string {
    return ADMIN_PREFIX === "local" ? "" : ADMIN_PREFIX;
  }

  /**
   *  Returns all Business documents
   */
  async getBusinesses(
    query?: string,
    limit?: number,
    continuationToken?: string
  ): Promise<
    ApiResult<CherryHubAdminApi.BusinessSummaryItemQueryResultSetResponse>
  > {
    const result = await this.adminApiClient.get("/business", {
      params: {
        q: query,
        limit: limit ?? 500,
        continuationToken,
      },
    });
    return apiResult<CherryHubAdminApi.BusinessSummaryItemQueryResultSetResponse>(
      result
    );
  }

  /**
   *  Looks up a Business through the abr.business.gov.au web service.
   */
  async lookupAbnRecord(
    method: string,
    query: string
  ): Promise<ApiResult<CherryHubAdminApi.ABNRecord[]>> {
    if (["abn", "acn", "name"].indexOf(method) === -1) {
      throw new Error(`Invalid ABN lookup type '${method}'`);
    }

    const result = await this.adminApiClient.get(`/business/lookup/${method}`, {
      params: { q: query },
    });

    return apiResult(result);
  }

  /**
   *  Creates a Business document.
   */
  async createBusiness(
    abnRecord: CherryHubAdminApi.ABNRecord
  ): Promise<ApiResult<CherryHubAdminApi.Business>> {
    const result = await this.adminApiClient.post("/business", abnRecord);

    return apiResult(result);
  }

  /**
   *  Update a Business document.
   */
  async updateBusiness(
    businessId: string,
    businessUpdate: Partial<CherryHubAdminApi.BusinessUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.Business>> {
    const result = await this.adminApiClient.patch(
      `/business/${businessId}`,
      businessUpdate
    );
    return apiResult(result);
  }

  /**
   * Returns a Business document by BusinessId.
   */
  async getBusiness(
    businessId: string
  ): Promise<ApiResult<CherryHubAdminApi.Business | null>> {
    const result = await this.adminApiClient.get(`/business/${businessId}`);

    return apiResult(result);
  }

  /**
   * Deletes a Business document by BusinessId. SysAdmin rights are required.
   */
  async deleteBusiness(businessId: string): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(`/business/${businessId}`);

    return apiResult(result);
  }

  /**
   *  Returns Venue documents by BusinessId.
   */
  async getBusinessVenuesResultSet(
    businessId: string,
    params: { q?: string; includeDeleted?: string; continuationToken?: string }
  ): Promise<
    ApiResult<CherryHubAdminApi.VenueSummaryItemQueryResultSetResponse>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/venues`,
      {
        params: {
          ...params,
          limit: 500,
        },
      }
    );

    return apiResult(result);
  }

  async getBusinessVenues(
    businessId: string,
    params: {
      q?: string;
      includeDeleted?: string;
      continuationToken?: string;
      limit?: number;
    }
  ): Promise<
    ApiResult<CherryHubAdminApi.VenueSummaryItemQueryResultSetResponse>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/venues`,
      {
        params: {
          ...params,
          limit: params.limit ?? 500,
        },
      }
    );

    return apiResult(result);
  }

  /**
   * Returns a Venue document by VenueId.
   */
  async getBusinessVenue(
    businessId: string,
    venueId: string
  ): Promise<ApiResult<CherryHubAdminApi.VenueDocument>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/venues/${venueId}`
    );

    return apiResult(result);
  }

  /**
   * Creates a Business Venue document.
   */
  async createBusinessVenue(
    businessId: string,
    venue: Partial<CherryHubAdminApi.VenueUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.VenueDocument>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/venues`,
      venue
    );

    return apiResult(result);
  }

  /**
   * Updates an existing Venue document by VenueId.
   */
  async updateBusinessVenue(
    businessId: string,
    venueId: string,
    venueUpdates: Partial<CherryHubAdminApi.VenueUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.VenueDocument>> {
    const result = await this.adminApiClient.patch(
      `/business/${businessId}/venues/${venueId}`,
      venueUpdates
    );

    return apiResult(result);
  }

  /**
   * Deletes a Business's Venue document by VenueId.
   */
  async deleteBusinessVenue(
    businessId: string,
    venueId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/venues/${venueId}`
    );

    return apiResult(result);
  }

  async restoreBusinessVenue(
    businessId: string,
    venueId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/venues/${venueId}`,
      { params: { undelete: "true" } }
    );

    return apiResult(result);
  }

  /**
   * Returns list of CherryHub.DataPlatform.v1.CherryPassConfigurationSummaryItem by BusinessId and PassConfigurationType.
   */
  async getBusinessDigitalMembershipCardPassConfiguration(
    businessId: string,
    query?: string,
    limit?: number,
    continuationToken?: string
  ): Promise<
    ApiResult<CherryHubAdminApi.CherryPassConfigurationSummaryItemQueryResultSetResponse>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/pass-configuration-summaries`,
      {
        params: {
          passConfigurationType: "DigitalMembershipCard",
          q: query,
          limit: limit ?? 500,
          continuationToken,
        },
      }
    );

    return apiResult(result);
  }

  /**
   * Returns a draft Pass Configuration by BusinessId and PassConfigurationId.
   */
  async getPassConfiguration(
    businessId: string,
    passConfigurationId: string
  ): Promise<ApiResult<CherryHubAdminApi.CherryPassConfigurationBasic>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/pass-configurations/${passConfigurationId}`
    );

    return apiResult(result);
  }

  /**
   * Creates a Pass Configuration document.
   */
  async createPassConfiguration(
    businessId: string,
    config: Partial<
      CherryHubAdminApi.CherryPassConfigurationSummaryItem & {
        isMembershipApplicationPass?: boolean;
      }
    >
  ): Promise<ApiResult<CherryHubAdminApi.CherryPassConfigurationSummaryItem>> {
    const formData = new FormData();
    formData.append("passConfigurationType", config.ConfigurationType!);
    formData.append("displayName", config.DisplayName!);
    formData.append("passDescription", config.PassDescription!);
    formData.append(
      "isMembershipApplicationPass",
      config.isMembershipApplicationPass ? "true" : "false"
    );
    const result = await this.adminApiClient.post(
      `/business/${businessId}/pass-configuration`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    );

    return apiResult(result);
  }

  /**
   * Clones a source pass configuration into a new pass configuration.
   */
  async clonePassConfiguration(
    businessId: string,
    request: CherryHubAdminApi.ClonePassConfigurationRequest
  ): Promise<ApiResult<CherryHubAdminApi.CherryPassConfigurationSummaryItem>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/pass-configurations/clone`,
      request
    );

    return apiResult(result);
  }

  /**
   * Copies selected pass configuration components from a source pass to one or more target passes.
   */
  async copyPassConfiguration(
    businessId: string,
    request: CherryHubAdminApi.CopyPassConfigurationRequest
  ): Promise<ApiResult<CherryHubAdminApi.CopyPassConfigurationResponse>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/pass-configurations/copy`,
      request
    );

    return apiResult(result);
  }

  /**
   * Publishes a new pass configuration version from a historical snapshot.
   * Image files are left as the latest published versions.
   */
  async revertPassConfiguration(
    businessId: string,
    passConfigurationId: string,
    historyId: string
  ): Promise<ApiResult<CherryHubAdminApi.CherryPassConfigurationBasic>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/pass-configurations/${passConfigurationId}/history/${encodeURIComponent(
        historyId
      )}/revert`
    );

    return apiResult(result);
  }

  /**
   * Returns history snapshots for a document, newest first.
   */
  async getDocumentHistory(
    businessId: string,
    documentType: string,
    documentId: string,
    limit?: number,
    continuationToken?: string
  ): Promise<
    ApiResult<CherryHubAdminApi.DocumentHistoryItemQueryResultSetResponse>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/documents/${documentType}/${documentId}/history`,
      {
        params: {
          limit: limit ?? 500,
          continuationToken,
        },
      }
    );

    return apiResult(result);
  }

  async getRedemptionFormConfigurations(
    businessId: string,
    params: { q?: string; includeDeleted?: string; continuationToken?: string }
  ): Promise<
    ApiResult<CherryHubAdminApi.RedemptionFormConfigurationResultSetResponse>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/redemption-form-configurations`,
      {
        params: {
          ...params,
          limit: 500,
        },
      }
    );

    return apiResult(result);
  }

  async getRedemptionFormConfiguration(
    businessId: string,
    redemptionFormConfigurationId: string
  ): Promise<ApiResult<CherryHubAdminApi.RedemptionFormConfigurationItem>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/redemption-form-configurations/${redemptionFormConfigurationId}`
    );
    return apiResult(result);
  }

  async editRedemptionFormConfiguration(
    businessId: string,
    redemptionFormConfigurationId: string,
    redemptionFormConfiguration: Partial<CherryHubAdminApi.RedemptionFormConfigurationUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.RedemptionFormConfigurationItem>> {
    const result = await this.adminApiClient.patch(
      `/business/${businessId}/redemption-form-configurations/${redemptionFormConfigurationId}`,
      redemptionFormConfiguration
    );
    return apiResult(result);
  }

  async addRedemptionFormConfiguration(
    businessId: string,
    redemptionFormConfiguration: Partial<CherryHubAdminApi.RedemptionFormConfigurationUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.RedemptionFormConfigurationItem>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/redemption-form-configurations`,
      redemptionFormConfiguration
    );
    return apiResult(result);
  }

  async deleteRedemptionFormConfiguration(
    businessId: string,
    redemptionFormConfigurationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/redemption-form-configurations/${redemptionFormConfigurationId}`
    );
    return apiResult(result);
  }

  async restoreRedemptionFormConfiguration(
    businessId: string,
    redemptionFormConfigurationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/redemption-form-configurations/${redemptionFormConfigurationId}?undelete=true`
    );
    return apiResult(result);
  }

  /**
   * Returns a Business's Integrations.
   */
  async getBusinessIntegrations(
    businessId: string,
    params: {
      q?: string;
      includeDeleted?: string;
      limit?: number;
      continuationToken?: string;
      sourceSystemName?: CherryHubAdminApi.SystemProvider;
      enabledService?: CherryHubAdminApi.ServiceType;
    }
  ): Promise<
    ApiResult<CherryHubAdminApi.IntegrationSummaryItemQueryResultSetResponse>
  > {
    params.limit = params.limit ?? 500;
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations`,
      {
        params: {
          ...params,
        },
      }
    );

    return apiResult(result);
  }

  /**
   * Returns an Integration document by IntegrationId.
   */
  async getBusinessIntegration(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<CherryHubAdminApi.Integration>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}`
    );

    return apiResult(result);
  }

  /**
   * Creates a Business Integration document.
   */
  async createBusinessIntegration(
    businessId: string,
    integration: CherryHubAdminApi.IntegrationUpdate
  ): Promise<ApiResult<CherryHubAdminApi.Integration>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/integrations`,
      integration
    );

    return apiResult(result);
  }

  /**
   * Deletes a Business's Integration document by IntegrationId.
   */
  async deleteBusinessIntegration(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/integrations/${integrationId}`
    );

    return apiResult(result);
  }

  async restoreBusinessIntegration(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/integrations/${integrationId}`,
      { params: { undelete: "true" } }
    );

    return apiResult(result);
  }

  /**
   * Updates an existing Integration document by IntegrationId.
   */
  async updateBusinessIntegration(
    businessId: string,
    integrationId: string,
    integrationUpdate: Partial<CherryHubAdminApi.IntegrationUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.Integration>> {
    const result = await this.adminApiClient.patch(
      `/business/${businessId}/integrations/${integrationId}`,
      integrationUpdate
    );

    return apiResult(result);
  }

  /**
   * Returns an enum collection/values by type.
   */
  async getEnumConfigurations(
    enumType: string
  ): Promise<ApiResult<CherryHubAdminApi.EnumConfigurationItem[]>> {
    const result = await this.adminApiClient.get(
      `/configuration/enums/${enumType}`
    );

    return apiResult(result);
  }

  async updateIntegrationConfiguration(
    businessId: string,
    integrationId: string,
    configuration: string | null
  ): Promise<ApiResult<{ [key: string]: unknown }>> {
    const result = await this.adminApiClient.put(
      `/business/${businessId}/integrations/${integrationId}/configuration`,
      configuration,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return apiResult(result);
  }

  async updateIntegrationDataStore(
    businessId: string,
    integrationId: string,
    dataStore: string | null
  ): Promise<ApiResult<{ [key: string]: unknown }>> {
    const result = await this.adminApiClient.put(
      `/business/${businessId}/integrations/${integrationId}/data-store`,
      dataStore,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return apiResult(result);
  }

  /**
   * Returns a Business's On-Demand Membership Registration Form Configuration.
   */
  async getBusinessRegistrationFormConfigurations(
    businessId: string,
    params: {
      q?: string;
      includeDeleted?: string;
      limit?: number;
      continuationToken?: string;
    }
  ): Promise<
    ApiResult<CherryHubAdminApi.RegistrationFormConfigurationSummaryItemQueryResultSetResponse>
  > {
    params.limit = params.limit ?? 500;
    const result = await this.adminApiClient.get(
      `/business/${businessId}/registration-form-configurations`,
      {
        params: {
          ...params,
        },
      }
    );

    return apiResult(result);
  }

  /**
   * Creates a Business On-Demand Membership Registration Form Configuration document.
   */
  async createBusinessRegistrationFormConfiguration(
    businessId: string,
    registration: CherryHubAdminApi.RegistratinFormConfigurationUpdate
  ): Promise<ApiResult<CherryHubAdminApi.RegistrationFormConfiguration>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/registration-form-configurations`,
      registration
    );

    return apiResult(result);
  }

  /**
   * Returns an On-Demand Membership Registration Form Configuration document by RegistrationFormConfigurationId.
   */
  async getBusinessRegistrationFormConfiguration(
    businessId: string,
    registrationId: string
  ): Promise<ApiResult<CherryHubAdminApi.RegistrationFormConfiguration>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/registration-form-configurations/${registrationId}`
    );

    return apiResult(result);
  }

  /**
   * Deletes a Business's On-Demand Membership Registration Form Configuration document by RegistrationFormConfigurationId.
   */
  async deleteBusinessRegistrationFormConfiguration(
    businessId: string,
    registrationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/registration-form-configurations/${registrationId}`
    );

    return apiResult(result);
  }

  async restoreBusinessRegistrationFormConfiguration(
    businessId: string,
    registrationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/registration-form-configurations/${registrationId}`,
      { params: { undelete: "true" } }
    );

    return apiResult(result);
  }

  /**
   * Clones an existing On-Demand Membership Registration Form Configuration document into a new one.
   */
  async cloneBusinessRegistrationFormConfiguration(
    businessId: string,
    registrationId: string
  ): Promise<ApiResult<CherryHubAdminApi.RegistrationFormConfiguration>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/registration-form-configurations/${registrationId}/clone`
    );

    return apiResult(result);
  }

  /**
   * Updates an existing RegistrationFormConfiguration document by RegistrationFormConfigurationId.
   */
  async updateBusinessRegistrationFormConfiguration(
    businessId: string,
    registrationId: string,
    registrationUpdate: Partial<CherryHubAdminApi.RegistratinFormConfigurationUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.RegistrationFormConfiguration>> {
    const result = await this.adminApiClient.patch(
      `/business/${businessId}/registration-form-configurations/${registrationId}`,
      registrationUpdate
    );

    return apiResult(result);
  }

  async uploadBusinessRegistrationFormConfigurationImage(
    businessId: string,
    registrationFormConfigurationId: string,
    registrationImageType: string,
    file: File
  ): Promise<ApiResult<string>> {
    const formData = new FormData();
    formData.append("file", file);
    const result = await this.adminApiClient.post(
      `/business/${businessId}/registration-form-configurations/${registrationFormConfigurationId}/images/${registrationImageType}`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    );

    return apiResult(result);
  }

  async deleteBusinessRegistrationFormConfigurationImage(
    businessId: string,
    registrationFormConfigurationId: string,
    registrationImageType: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/registration-form-configurations/${registrationFormConfigurationId}/images/${registrationImageType}`
    );

    return apiResult(result);
  }

  async getMembershipRenewalFormConfigurations(
    businessId: string,
    params: { q?: string; includeDeleted?: string; continuationToken?: string }
  ): Promise<
    ApiResult<CherryHubAdminApi.MembershipRenewalFormConfigurationResultSetResponse>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/membership-renewal-form-configurations`,
      {
        params: {
          ...params,
          limit: 500,
        },
      }
    );

    return apiResult(result);
  }

  async getMembershipRenewalFormConfiguration(
    businessId: string,
    membershipRenewalFormConfigurationId: string
  ): Promise<
    ApiResult<CherryHubAdminApi.MembershipRenewalFormConfigurationItem>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/membership-renewal-form-configurations/${membershipRenewalFormConfigurationId}`
    );
    return apiResult(result);
  }

  async addMembershipRenewalFormConfiguration(
    businessId: string,
    membershipRenewalData: Partial<CherryHubAdminApi.MembershipRenewalFormConfigurationUpdate>
  ): Promise<
    ApiResult<CherryHubAdminApi.MembershipRenewalFormConfigurationItem>
  > {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/membership-renewal-form-configurations`,
      membershipRenewalData
    );
    return apiResult(result);
  }

  async editMembershipRenewalFormConfiguration(
    businessId: string,
    membershipRenewalId: string,
    membershipRenewalData: Partial<CherryHubAdminApi.MembershipRenewalFormConfigurationUpdate>
  ): Promise<
    ApiResult<CherryHubAdminApi.MembershipRenewalFormConfigurationItem>
  > {
    const result = await this.adminApiClient.patch(
      `/business/${businessId}/membership-renewal-form-configurations/${membershipRenewalId}`,
      membershipRenewalData
    );
    return apiResult(result);
  }

  async deleteMembershipRenewalFormConfiguration(
    businessId: string,
    membershipRenewalFormConfigurationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/membership-renewal-form-configurations/${membershipRenewalFormConfigurationId}`
    );
    return apiResult(result);
  }

  async restoreMembershipRenewalFormConfiguration(
    businessId: string,
    membershipRenewalFormConfigurationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/membership-renewal-form-configurations/${membershipRenewalFormConfigurationId}?undelete=true`
    );
    return apiResult(result);
  }

  async getLandingPageConfigurations(
    businessId: string,
    params: { q?: string; includeDeleted?: string; continuationToken?: string }
  ): Promise<
    ApiResult<CherryHubAdminApi.LandingPageConfigurationResultSetResponse>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/landing-page-configurations`,
      {
        params: {
          ...params,
          limit: 500,
        },
      }
    );

    return apiResult(result);
  }

  async getLandingPageConfiguration(
    businessId: string,
    landingPageConfigurationId: string
  ): Promise<ApiResult<CherryHubAdminApi.LandingPageConfigurationItem>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/landing-page-configurations/${landingPageConfigurationId}`
    );
    return apiResult(result);
  }

  async addLandingPageConfiguration(
    businessId: string,
    landingPageConfigurationData: Partial<CherryHubAdminApi.LandingPageConfigurationUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.LandingPageConfigurationItem>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/landing-page-configurations`,
      landingPageConfigurationData
    );
    return apiResult(result);
  }

  async editLandingPageConfiguration(
    businessId: string,
    landingPageConfigurationId: string,
    landingPageConfigurationData: Partial<CherryHubAdminApi.LandingPageConfigurationUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.LandingPageConfigurationItem>> {
    const result = await this.adminApiClient.patch(
      `/business/${businessId}/landing-page-configurations/${landingPageConfigurationId}`,
      landingPageConfigurationData
    );
    return apiResult(result);
  }

  async deleteLandingPageConfiguration(
    businessId: string,
    landingPageConfigurationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/landing-page-configurations/${landingPageConfigurationId}`
    );
    return apiResult(result);
  }

  async restoreLandingPageConfiguration(
    businessId: string,
    landingPageConfigurationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/landing-page-configurations/${landingPageConfigurationId}?undelete=true`
    );
    return apiResult(result);
  }

  async getMemberSelfServiceConfigurations(
    businessId: string,
    params: { q?: string; includeDeleted?: string; continuationToken?: string }
  ): Promise<
    ApiResult<CherryHubAdminApi.MemberSelfServiceConfigurationResultSetResponse>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/member-self-service-configurations`,
      {
        params: {
          ...params,
          limit: 500,
        },
      }
    );
    return apiResult(result);
  }

  async getMemberSelfServiceConfiguration(
    businessId: string,
    memberSelfServiceConfigurationId: string
  ): Promise<
    ApiResult<CherryHubAdminApi.MemberSelfServiceConfigurationUpdate>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/member-self-service-configurations/${memberSelfServiceConfigurationId}`
    );
    return apiResult(result);
  }

  async addMemberSelfServiceConfiguration(
    businessId: string,
    data: Partial<CherryHubAdminApi.MemberSelfServiceConfigurationUpdate>
  ): Promise<
    ApiResult<CherryHubAdminApi.MemberSelfServiceConfigurationUpdate>
  > {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/member-self-service-configurations`,
      data
    );
    return apiResult(result);
  }

  async editMemberSelfServiceConfiguration(
    businessId: string,
    memberSelfServiceConfigurationId: string,
    data: Partial<CherryHubAdminApi.MemberSelfServiceConfigurationUpdate>
  ): Promise<
    ApiResult<CherryHubAdminApi.MemberSelfServiceConfigurationUpdate>
  > {
    const result = await this.adminApiClient.patch(
      `/business/${businessId}/member-self-service-configurations/${memberSelfServiceConfigurationId}`,
      data
    );
    return apiResult(result);
  }

  async deleteMemberSelfServiceConfiguration(
    businessId: string,
    memberSelfServiceConfigurationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/member-self-service-configurations/${memberSelfServiceConfigurationId}`
    );
    return apiResult(result);
  }

  async restoreMemberSelfServiceConfiguration(
    businessId: string,
    memberSelfServiceConfigurationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/member-self-service-configurations/${memberSelfServiceConfigurationId}?undelete=true`
    );
    return apiResult(result);
  }

  /**
   * Returns Member's data by fieldName
   */
  async getMemberDataOptions(
    businessId: string,
    fieldName: string,
    venueId?: string
  ): Promise<ApiResult<CherryHubAdminApi.ScalarValue[]>> {
    const result = await this.adminApiClient.get(
      `/configuration/${businessId}/member-data/${fieldName}`,
      {
        params: {
          venueId: venueId,
        },
      }
    );
    return apiResult(result);
  }

  async generateNonComparativeReport(
    reportType: ReportType,
    startDate?: string | null,
    endDate?: string | null,
    businessIds?: string[] | null
  ): Promise<ApiResult<string>> {
    const result = await this.adminApiClient.get(`/reports/${reportType}/csv`, {
      params: {
        startDate,
        endDate,
        businessIds: businessIds?.join(","),
      },
      responseType: "blob",
    });

    if (result.status < 300) {
      const url = URL.createObjectURL(result.data);
      const response: ApiResult<string> = {
        data: url,
        ok: true,
        statusCode: result.status,
      };
      return response;
    }
    return apiResult(result);
  }

  async generateComparativeReport(
    reportType: ReportType,
    period1StartDate?: string | null,
    period1EndDate?: string | null,
    period2StartDate?: string | null,
    period2EndDate?: string | null,
    businessIds?: string[] | null
  ): Promise<ApiResult<string>> {
    const result = await this.adminApiClient.get(
      `/reports/${reportType}/comparative-report/csv`,
      {
        params: {
          period1StartDate,
          period1EndDate,
          period2StartDate,
          period2EndDate,
          businessIds: businessIds?.join(","),
        },
        responseType: "blob",
      }
    );

    if (result.status < 300) {
      const url = URL.createObjectURL(result.data);
      const response: ApiResult<string> = {
        data: url,
        ok: true,
        statusCode: result.status,
      };
      return response;
    }
    return apiResult(result);
  }

  async getCherryPassStatus(params: {
    businessIds?: string;
    invitationType?: InvitationType;
  }): Promise<ApiResult<CherryHubAdminApi.CherryPassStatusSummary[]>> {
    const result = await this.adminApiClient.get(
      `/reports/cherry-pass-status`,
      {
        params: {
          ...params,
        },
      }
    );

    return apiResult(result);
  }

  /**
   * Returns ODMA Url based on type
   */
  async getOdmaUrl(
    businessId: string,
    type: CherryHubAdminApi.OdmaUrlType,
    id: string
  ): Promise<ApiResult<CherryHubAdminApi.ScalarValue>> {
    const result = await this.adminApiClient.get(
      `/configuration/${businessId}/odma-url/${type}/${id}`
    );

    return apiResult(result);
  }

  /**
   * Returns Platform Applications.
   */
  async getPlatformApplications(params: {
    q?: string;
    limit?: number;
    continuationToken?: string;
    includeDeleted?: boolean;
  }): Promise<
    ApiResult<CherryHubAdminApi.PlatformApplicationSummaryItemQueryResultSetResponse>
  > {
    params.limit = params.limit ?? 500;
    const result = await this.adminApiClient.get(`/applications`, {
      params: {
        ...params,
      },
    });

    return apiResult(result);
  }

  /**
   * Returns an Application document by ApplicationId.
   */
  async getPlatformApplication(
    applicationId: string
  ): Promise<ApiResult<CherryHubAdminApi.PlatformApplication>> {
    const result = await this.adminApiClient.get(
      `/applications/${applicationId}`
    );

    return apiResult(result);
  }

  /**
   * Creates an Application document.
   */
  async createPlatformApplication(
    application: CherryHubAdminApi.PlatformApplicationUpdate
  ): Promise<ApiResult<CherryHubAdminApi.PlatformApplication>> {
    const result = await this.adminApiClient.post(`/applications`, application);

    return apiResult(result);
  }

  /**
   * Deletes an Application document by ApplicationId.
   */
  async deletePlatformApplication(
    applicationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/applications/${applicationId}`
    );

    return apiResult(result);
  }

  async restorePlatformApplication(
    applicationId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/applications/${applicationId}`,
      { params: { undelete: "true" } }
    );

    return apiResult(result);
  }

  /**
   * Updates an existing Application document by ApplicationId.
   */
  async updatePlatformApplication(
    applicationId: string,
    applicationUpdate: Partial<CherryHubAdminApi.PlatformApplicationUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.PlatformApplication>> {
    const result = await this.adminApiClient.patch(
      `/applications/${applicationId}`,
      applicationUpdate
    );

    return apiResult(result);
  }

  /**
   * Returns Application's Permissions.
   */
  async getApplicationPermissions(): Promise<
    ApiResult<CherryHubAdminApi.ApplicationPermission[]>
  > {
    const result = await this.adminApiClient.get(
      `/configuration/application-permissions`
    );

    return apiResult(result);
  }

  /**
   * Gets Filtered Users from the Authentication database.
   */
  async getUsers(params: {
    uid?: string;
    businessId?: string;
    emailAddressContains?: string;
    phoneNumberContains?: string;
    displayNameContains?: string;
    customClaimKeys?: string;
    customClaimValueContains?: string;
    isDisabledEquals?: boolean;
    includeServiceAccount?: boolean;
    q?: string;
    limit?: number;
    continuationToken?: string;
  }): Promise<
    ApiResult<CherryHubAdminApi.UserAccountSummaryItemQueryResultSetResponse>
  > {
    params.limit = params.limit ?? 500;
    const result = await this.adminApiClient.get(`/auth/users`, {
      params: {
        ...params,
      },
    });

    return apiResult(result);
  }

  /**
   * Gets User By uId from the Authentication database.
   */
  async getUser(
    uid: string
  ): Promise<ApiResult<CherryHubAdminApi.UserAccount>> {
    const result = await this.adminApiClient.get(`/auth/users/${uid}`);

    return apiResult(result);
  }

  /**
   * Creates a User document.
   */
  async createUser(
    user: Partial<CherryHubAdminApi.NewUserAccount>
  ): Promise<ApiResult<CherryHubAdminApi.UserAccount>> {
    const result = await this.adminApiClient.post(`/auth/users`, user);

    return apiResult(result);
  }

  /**
   * Deletes a User document by UserId.
   */
  async deleteUser(
    uid: string,
    query: {
      id: string;
    }
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(`/auth/users/${uid}`, {
      params: {
        ...query,
      },
    });

    return apiResult(result);
  }

  /**
   * Updates an existing User by userId.
   */
  async updateUser(
    uid: string,
    userUpdate: Partial<CherryHubAdminApi.UserAccountUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.UserAccount>> {
    const result = await this.adminApiClient.patch(
      `/auth/users/${uid}`,
      userUpdate
    );

    return apiResult(result);
  }

  /**
   * Enables a User in the Authentication database.
   */
  async enableUser(uid: string): Promise<ApiResult<boolean>> {
    const result = await this.adminApiClient.post(`/auth/users/${uid}/enable`);

    return apiResult(result);
  }

  /**
   * Disables a User in the Authentication database.
   */
  async disableUser(uid: string): Promise<ApiResult<boolean>> {
    const result = await this.adminApiClient.post(`/auth/users/${uid}/disable`);

    return apiResult(result);
  }

  async updateUserClaim(
    uid: string,
    claimName: string,
    claimValue: any
  ): Promise<ApiResult<CherryHubAdminApi.UserAccount>> {
    const result = await this.adminApiClient.put(
      `/auth/users/${uid}/claim/${claimName}`,
      claimValue,
      {
        headers: {
          "content-type": "application/json",
        },
      }
    );

    return apiResult(result);
  }

  async deleteUserClaim(
    uid: string,
    claimName: string
  ): Promise<ApiResult<CherryHubAdminApi.UserAccount>> {
    const result = await this.adminApiClient.delete(
      `/auth/users/${uid}/claim/${claimName}`
    );

    return apiResult(result);
  }

  async resetUserPassword(uid: string): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.post(
      `/auth/users/${uid}/reset-password`
    );

    return apiResult(result);
  }

  async getMembers(params: {
    businessId?: string;
    q?: string;
    continuationToken?: string;
    limit?: number;
  }): Promise<
    ApiResult<CherryHubAdminApi.MemberSummaryItemQueryResultSetResponse>
  > {
    const result = await this.adminApiClient.get(`/business/members`, {
      params: {
        ...params,
        limit: 500,
      },
    });

    return apiResult(result);
  }

  async getMemberDetail(
    memberId: string,
    businessId?: string | undefined
  ): Promise<ApiResult<CherryHubAdminApi.Member>> {
    const result = await this.adminApiClient.get(
      `/business/members/${memberId}${
        businessId ? `?businessId=${businessId}` : ""
      }`
    );

    return apiResult(result);
  }

  async getCherryPayCards(params: {
    businessId?: string;
    externalAccountId?: string;
    q?: string;
    limit?: number;
    continuationToken?: string;
    createdFromDate?: string;
    createdToDate?: string;
  }): Promise<
    ApiResult<CherryHubAdminApi.CherryPayCardCompactQueryResultSetResponse>
  > {
    params.limit = params.limit ?? 500;
    const result = await this.adminApiClient.get(`/cherrypay-cards`, {
      params: { ...params },
    });
    return apiResult(result);
  }

  async getCherryPayCard(
    cardId: string
  ): Promise<ApiResult<CherryHubAdminApi.CherryPayCard>> {
    const result = await this.adminApiClient.get(`/cherrypay-cards/${cardId}`);
    return apiResult(result);
  }

  async getCherryPayCardTransactions(
    cardId: string,
    params?: {
      limit?: number;
      continuationToken?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<
    ApiResult<CherryHubAdminApi.CherryPayCardTransactionQueryResultSetResponse>
  > {
    const result = await this.adminApiClient.get(
      `/cherrypay-cards/${cardId}/transactions`,
      { params: { ...params } }
    );
    return apiResult(result);
  }

  async getCherryPayCardTransactionsCsv(
    cardId: string,
    params?: { startDate?: string; endDate?: string }
  ): Promise<ApiResult<Blob>> {
    const result = await this.adminApiClient.get(
      `/cherrypay-cards/${cardId}/transactions/csv`,
      {
        params: params
          ? { startDate: params.startDate, endDate: params.endDate }
          : {},
        responseType: "blob",
      }
    );
    return apiResult(result);
  }

  async getDigitalMembershipCardActiveMemberStatus(params: {
    businessIds?: string;
  }): Promise<
    ApiResult<CherryHubAdminApi.DigitalMembershipCardStatusReportItem[]>
  > {
    const result = await this.adminApiClient.get(
      `/reports/digital-membership-card-active-member-status`,
      {
        params: {
          ...params,
        },
      }
    );

    return apiResult(result);
  }

  async getDigitalMembershipCardActiveMemberStatusCsv(params: {
    businessIds?: string;
  }) {
    const result = await this.adminApiClient.get(
      `/reports/digital-membership-card-active-member-status/csv`,
      {
        params: {
          ...params,
        },
      }
    );

    return apiResult<string>(result);
  }

  async getEmailTemplates(
    filters?: string
  ): Promise<ApiResult<CherryHubAdminApi.IdNameSet[]>> {
    const result = await this.adminApiClient.get(
      `/configuration/email-templates`,
      {
        params: {
          filters,
        },
      }
    );

    return apiResult(result);
  }

  /**
   * Returns an EMLConfiguration document by businessId.
   */
  async getEMLConfiguration(
    businessId: string
  ): Promise<ApiResult<CherryHubAdminApi.EMLConfiguration>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/eml-configuration`
    );
    return apiResult(result);
  }

  /**
   * Returns an EventsModuleConfiguration document by businessId.
   */
  async getEventsModuleConfiguration(
    businessId: string
  ): Promise<ApiResult<CherryHubAdminApi.EventsModuleConfiguration>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/events-module-configuration`
    );
    return apiResult(result);
  }

  /**
   * Creates or updates an EventsModuleConfiguration document.
   */
  async createOrUpdateEventsModuleConfiguration(
    businessId: string,
    eventsModuleConfiguration: CherryHubAdminApi.EventsModuleConfigurationUpdate
  ): Promise<ApiResult<CherryHubAdminApi.EventsModuleConfiguration>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/events-module-configuration`,
      eventsModuleConfiguration
    );
    return apiResult(result);
  }

  /**
   * Returns a PushNotificationsConfiguration document by businessId.
   */
  async getPushNotificationsConfiguration(
    businessId: string
  ): Promise<ApiResult<CherryHubAdminApi.PushNotificationsConfiguration>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/push-notifications-configuration`
    );
    return apiResult(result);
  }

  /**
   * Creates or updates a PushNotificationsConfiguration document.
   */
  async createOrUpdatePushNotificationsConfiguration(
    businessId: string,
    pushNotificationsConfiguration: CherryHubAdminApi.PushNotificationsConfigurationUpdate
  ): Promise<ApiResult<CherryHubAdminApi.PushNotificationsConfiguration>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/push-notifications-configuration`,
      pushNotificationsConfiguration
    );
    return apiResult(result);
  }

  /**
   * Backfills GeoLocationGroupId on every PassConfiguration owned by the business.
   */
  async migrateLocationGroups(
    businessId: string
  ): Promise<ApiResult<CherryHubAdminApi.LocationGroupMigrationSummary>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/migrate-location-groups`
    );
    return apiResult(result);
  }

  /**
   * Clears GeoLocationGroupId on every PassConfiguration owned by the business.
   */
  async unMigrateLocationGroups(
    businessId: string
  ): Promise<ApiResult<CherryHubAdminApi.LocationGroupUnMigrationSummary>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/un-migrate-location-groups`
    );
    return apiResult(result);
  }

  /**
   * Returns all GeoLocation records for the given business.
   */
  async getGeoLocations(
    businessId: string
  ): Promise<ApiResult<CherryHubAdminApi.GeoLocation[]>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/pass-configuration/geo-locations`
    );
    return apiResult(result);
  }

  /**
   * Returns the GeoLocation record matching the global document id.
   */
  async getGeoLocation(
    businessId: string,
    id: string
  ): Promise<ApiResult<CherryHubAdminApi.GeoLocation>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/pass-configuration/geo-locations/${id}`
    );
    return apiResult(result);
  }

  /**
   * Creates a new GeoLocation for the given business.
   */
  async createGeoLocation(
    businessId: string,
    update: CherryHubAdminApi.GeoLocationUpdate
  ): Promise<ApiResult<CherryHubAdminApi.GeoLocation>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/pass-configuration/geo-locations`,
      update
    );
    return apiResult(result);
  }

  /**
   * Updates the GeoLocation matching the global document id.
   */
  async updateGeoLocation(
    businessId: string,
    id: string,
    update: CherryHubAdminApi.GeoLocationUpdate
  ): Promise<ApiResult<CherryHubAdminApi.GeoLocation>> {
    const result = await this.adminApiClient.put(
      `/business/${businessId}/pass-configuration/geo-locations/${id}`,
      update
    );
    return apiResult(result);
  }

  /**
   * Deletes the GeoLocation matching the global document id.
   */
  async deleteGeoLocation(
    businessId: string,
    id: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/pass-configuration/geo-locations/${id}`
    );
    return apiResult(result);
  }

  /**
   * Returns all GeoLocationGroup records for the given business.
   */
  async getGeoLocationGroups(
    businessId: string
  ): Promise<ApiResult<CherryHubAdminApi.GeoLocationGroup[]>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/pass-configuration/geo-location-groups`
    );
    return apiResult(result);
  }

  /**
   * Returns the GeoLocationGroup record matching the global document id.
   */
  async getGeoLocationGroup(
    businessId: string,
    id: string
  ): Promise<ApiResult<CherryHubAdminApi.GeoLocationGroup>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/pass-configuration/geo-location-groups/${id}`
    );
    return apiResult(result);
  }

  /**
   * Creates a new GeoLocationGroup for the given business.
   */
  async createGeoLocationGroup(
    businessId: string,
    update: CherryHubAdminApi.GeoLocationGroupUpdate
  ): Promise<ApiResult<CherryHubAdminApi.GeoLocationGroup>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/pass-configuration/geo-location-groups`,
      update
    );
    return apiResult(result);
  }

  /**
   * Updates the GeoLocationGroup matching the global document id.
   */
  async updateGeoLocationGroup(
    businessId: string,
    id: string,
    update: CherryHubAdminApi.GeoLocationGroupUpdate
  ): Promise<ApiResult<CherryHubAdminApi.GeoLocationGroup>> {
    const result = await this.adminApiClient.put(
      `/business/${businessId}/pass-configuration/geo-location-groups/${id}`,
      update
    );
    return apiResult(result);
  }

  /**
   * Deletes the GeoLocationGroup matching the global document id.
   */
  async deleteGeoLocationGroup(
    businessId: string,
    id: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/pass-configuration/geo-location-groups/${id}`
    );
    return apiResult(result);
  }

  /**
   * Creates a Business EMLConfiguration document.
   */
  async createEMLConfiguration(
    businessId: string,
    emlConfiguration: Partial<CherryHubAdminApi.EMLConfigurationUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.EMLConfiguration>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/eml-configuration`,
      emlConfiguration
    );
    return apiResult(result);
  }

  /**
   * Updates an existing EMLConfiguration document by businessId.
   */
  async updateEMLConfiguration(
    businessId: string,
    emlConfiguration: Partial<CherryHubAdminApi.EMLConfigurationUpdate>
  ): Promise<ApiResult<CherryHubAdminApi.EMLConfiguration>> {
    const result = await this.adminApiClient.patch(
      `/business/${businessId}/eml-configuration`,
      emlConfiguration
    );
    return apiResult(result);
  }

  /**
   * Returns CherryPay Self-Serve Member Terminal Url with the required parameters
   */
  async getCherryPaySelfServeMemberTerminalUrl(
    businessId: string,
    terminalId: number,
    checkCode: string
  ): Promise<ApiResult<CherryHubAdminApi.ScalarValue>> {
    const result = await this.adminApiClient.get(
      `/configuration/${businessId}/eml-configuration/self-serve-member-terminal-url/${terminalId}/${checkCode}`
    );
    return apiResult(result);
  }

  /**
   * Returns Messages Usages for every Sub Accounts by TwilioConfigurationId.
   */
  async getTwilioBillingUsages(
    twilioConfigurationId: string,
    month: number,
    year: number
  ): Promise<ApiResult<CherryHubAdminApi.TwilioBillingAccount[]>> {
    const result = await this.adminApiClient.get(
      `/reports/twilio-billing-usages/${twilioConfigurationId}`,
      {
        params: {
          month,
          year,
        },
      }
    );
    return apiResult(result);
  }

  /**
   * Returns form fields.
   */
  async getMMSFormFields(
    businessId: string,
    integrationId: string
  ): Promise<
    ApiResult<{ [key: string]: CherryHubAdminApi.CustomFormField[] }>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/membership-management-service/form-fields`
    );
    return apiResult(result);
  }

  /**
   * Returns data list.
   */
  async getMMSFormFieldKeyValuePairs(
    businessId: string,
    integrationId: string,
    fieldId: string
  ): Promise<ApiResult<{ Key: string; Value: string }[]>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/membership-management-service/form-fields/${fieldId}/values`
    );
    return apiResult(result);
  }

  /**
   * Validate Form Fields values.
   */
  async validateMMSFormFieldKeyValuePairs(
    businessId: string,
    integrationId: string,
    fieldId: string,
    keys: string[]
  ): Promise<ApiResult<CherryHubAdminApi.FormFieldValidationResult>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/integrations/${integrationId}/membership-management-service/form-fields/${fieldId}/values/validate`,
      keys
    );
    return apiResult(result);
  }

  /**
   * Returns form field Fee Amounts data.
   */
  async getMMSFeeAmounts(
    businessId: string,
    integrationId: string,
    fieldId: string
  ): Promise<ApiResult<{ [key: string]: CherryHubAdminApi.FeeDetail }>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/membership-management-service/form-fields/${fieldId}/fee-amounts`
    );
    return apiResult(result);
  }

  /**
   * Validate Form Fields.
   */
  async validateMMSFormField(
    businessId: string,
    integrationId: string,
    formFields: CherryHubAdminApi.CustomFormField[]
  ): Promise<ApiResult<CherryHubAdminApi.FormFieldValidationResult>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/integrations/${integrationId}/membership-management-service/form-fields/validate`,
      formFields
    );
    return apiResult(result);
  }

  /**
   * Returns service managament metadata by businessId and integrationId
   */
  async getServiceManagementMetadata(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<CherryHubAdminApi.ServiceManagementMetadata>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/service-management-metadata`
    );
    return apiResult(result);
  }

  /**
   * Bootstraps the Integration's Configuration, if it supports it.
   */
  async bootstrapIntegrationConfiguration(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<{ [key: string]: unknown }>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/integrations/${integrationId}/bootstrap-configuration`
    );
    return apiResult(result);
  }

  async getIntegrationPermissions(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<CherryHubAdminApi.ServiceManagementPermissions>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/permissions`
    );
    return apiResult(result);
  }

  async updateIntegrationPermissions(
    businessId: string,
    integrationId: string,
    permission: Partial<CherryHubAdminApi.ServiceManagementPermissions>
  ): Promise<ApiResult<CherryHubAdminApi.ServiceManagementPermissions>> {
    const result = await this.adminApiClient.put(
      `/business/${businessId}/integrations/${integrationId}/permissions`,
      permission
    );
    return apiResult(result);
  }

  async getIntegrationEnvironment(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<CherryHubDataApi.Environment>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/environment`
    );
    return apiResult(result);
  }

  async getIntegrationEnvironmentConfiguration(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<CherryHubDataApi.Configurations>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/environment/configuration`
    );
    return apiResult(result);
  }

  async getIntegrationOutboundIpDetails(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<[key: string]>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/environment/outbound-ip-details`
    );
    return apiResult(result);
  }

  /**
   * Returns mms list types by businessId and integrationId
   */
  async getIntegrationMMSListTypes(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<string[]>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/mms/list-types`
    );
    return apiResult(result);
  }

  /**
   * Returns mms list all by businessId and integrationId
   */
  async getIntegrationMMSListAll(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<{ [key: string]: any[] }>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/mms/list/--all`
    );
    return apiResult(result);
  }

  /**
   * Returns mms list by businessId, integrationId and listTypeId
   */
  async getIntegrationMMSList(
    businessId: string,
    integrationId: string,
    listTypeId: string
  ): Promise<ApiResult<string[]>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/mms/list/${listTypeId}`
    );
    return apiResult(result);
  }

  /**
   * Perform MMS connection test by businessId and integrationId
   */
  async getIntegrationMMSConnectionTest(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<CherryHubAdminApi.ConnectionTest>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/integrations/${integrationId}/mms/connection-test`
    );
    return apiResult(result);
  }

  /**
   * Perform MMS connection close by businessId and integrationId
   */
  async getIntegrationMMSConnectionClose(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<CherryHubAdminApi.ScalarValue>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/integrations/${integrationId}/mms/connection-close`
    );
    return apiResult(result);
  }

  /**
   * Perform MMS connection status by businessId and integrationId
   */
  async getIntegrationMMSConnectionStatus(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<boolean>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/mms/connection-status`
    );
    return apiResult(result);
  }

  /**
   * Perform MLS connection test by businessId and integrationId
   */
  async getIntegrationMLSConnectionTest(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<CherryHubAdminApi.ConnectionTest>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/integrations/${integrationId}/mls/connection-test`
    );
    return apiResult(result);
  }

  /**
   * Perform MLS connection close by businessId and integrationId
   */
  async getIntegrationMLSConnectionClose(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<CherryHubAdminApi.ScalarValue>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/integrations/${integrationId}/mls/connection-close`
    );
    return apiResult(result);
  }

  /**
   * Perform MLS connection status by businessId and integrationId
   */
  async getIntegrationMLSConnectionStatus(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<boolean>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/mls/connection-status`
    );
    return apiResult(result);
  }

  /**
   * Returns worker list by businessId, integrationId and listTypeId
   */
  async getIntegrationWorkerList(
    businessId: string,
    integrationId: string,
    listTypeId: string
  ): Promise<ApiResult<string[]>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/worker/list/${listTypeId}`
    );
    return apiResult(result);
  }

  /**
   * Returns worker list all list types by businessId and integrationId
   */
  async getIntegrationWorkerListAll(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<{ [key: string]: any[] }>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/worker/list/--all`
    );
    return apiResult(result);
  }

  /**
   * Returns worker list types by businessId and integrationId
   */
  async getIntegrationWorkerListTypes(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<string[]>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/worker/list-types`
    );
    return apiResult(result);
  }

  /**
   * Perform worker start by businessId and integrationId
   */
  async getIntegrationWorkerStart(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<{ [key: string]: any }>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/integrations/${integrationId}/worker/start`
    );
    return apiResult(result);
  }

  /**
   * Perform worker abort by businessId and integrationId
   */
  async getIntegrationWorkerAbort(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<boolean>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/integrations/${integrationId}/worker/abort`
    );
    return apiResult(result);
  }

  /**
   * Returns worker status by businessId, integrationId and executionId
   */
  async getIntegrationWorkerStatus(
    businessId: string,
    integrationId: string,
    executionId: string
  ): Promise<ApiResult<{ [key: string]: any }>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/worker/status/${executionId}`
    );
    return apiResult(result);
  }

  /**
   * Returns worker status all by businessId and integrationId
   */
  async getIntegrationWorkerStatusAll(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<{ [key: string]: CherryHubAdminApi.ServiceStatus }>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/worker/status/--all`
    );
    return apiResult(result);
  }

  /**
   * Returns integration worker secret by businessId and integrationId
   */
  async getIntegrationSecret(
    businessId: string,
    integrationId: string
  ): Promise<ApiResult<CherryHubAdminApi.IntegrationSecretSpec[]>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/integrations/${integrationId}/integration-secrets`
    );
    return apiResult(result);
  }

  /**
   * Update integration secret by businessId, integrationId and credentialName
   */
  async updateIntegrationSecret(
    businessId: string,
    integrationId: string,
    credentialName: string,
    value: string
  ): Promise<ApiResult<boolean>> {
    const result = await this.adminApiClient.put(
      `/business/${businessId}/integrations/${integrationId}/integration-secrets/${credentialName}`,
      // The endpoint binds the body as a string, so it has to be a JSON string literal.
      JSON.stringify(value),
      {
        headers: {
          "content-type": "application/json",
        },
      }
    );
    return apiResult(result);
  }

  /**
   * Delete integration secret by businessId, integrationId and credentialName
   */
  async deleteIntegrationSecret(
    businessId: string,
    integrationId: string,
    credentialName: string
  ): Promise<ApiResult<boolean>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/integrations/${integrationId}/integration-secrets/${credentialName}`
    );
    return apiResult(result);
  }

  /**
   * Returns all Extended Field definitions for a business.
   */
  async getBusinessExtendedFields(
    businessId: string,
    includeDeleted?: boolean
  ): Promise<ApiResult<CherryHubAdminApi.ExtendedFieldDefinition[]>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/extended-fields?includeDeleted=${!!includeDeleted}`
    );
    return apiResult(result);
  }

  /**
   * Returns an Extended Field definition by fieldId.
   */
  async getBusinessExtendedField(
    businessId: string,
    fieldId: string
  ): Promise<ApiResult<CherryHubAdminApi.ExtendedFieldDefinition>> {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/extended-fields/${fieldId}`
    );
    return apiResult(result);
  }

  /**
   * Creates a new Extended Field definition.
   */
  async createBusinessExtendedField(
    businessId: string,
    field: Partial<CherryHubAdminApi.CreateExtendedFieldDefinition>
  ): Promise<ApiResult<CherryHubAdminApi.ExtendedFieldDefinition>> {
    const result = await this.adminApiClient.post(
      `/business/${businessId}/extended-fields`,
      field
    );
    return apiResult(result);
  }

  /**
   * Updates an existing Extended Field definition by fieldId.
   */
  async updateBusinessExtendedField(
    businessId: string,
    fieldId: string,
    fieldUpdate: Partial<CherryHubAdminApi.UpdateExtendedFieldDefinition>
  ): Promise<ApiResult<CherryHubAdminApi.ExtendedFieldDefinition>> {
    const result = await this.adminApiClient.put(
      `/business/${businessId}/extended-fields/${fieldId}`,
      fieldUpdate
    );
    return apiResult(result);
  }

  /**
   * Deletes an Extended Field definition by fieldId.
   */
  async deleteBusinessExtendedField(
    businessId: string,
    fieldId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/extended-fields/${fieldId}`
    );
    return apiResult(result);
  }

  /**
   * Restores a soft-deleted Extended Field definition by fieldId.
   */
  async restoreBusinessExtendedField(
    businessId: string,
    fieldId: string
  ): Promise<ApiResult<any>> {
    const result = await this.adminApiClient.delete(
      `/business/${businessId}/extended-fields/${fieldId}`,
      { params: { undelete: "true" } }
    );
    return apiResult(result);
  }

  /**
   * Updates the order of Extended Field definitions.
   */
  async updateBusinessExtendedFieldOrder(
    businessId: string,
    orderedFieldIds: string[]
  ): Promise<ApiResult<boolean>> {
    const result = await this.adminApiClient.patch(
      `/business/${businessId}/extended-fields/order`,
      { OrderedFieldIds: orderedFieldIds }
    );
    return apiResult(result);
  }

  /**
   * Returns registration forms that use the specified extended field.
   */
  async getRegistrationFormsForExtendedField(
    businessId: string,
    fieldId: string
  ): Promise<
    ApiResult<CherryHubAdminApi.RegistrationFormConfigurationSummaryItem[]>
  > {
    const result = await this.adminApiClient.get(
      `/business/${businessId}/extended-fields/${fieldId}/registration-forms`
    );
    return apiResult(result);
  }
}
